/**
 * Decision Memory Service
 * 
 * Makes C3.F3 (Decision memory reconstructs without oral history) true.
 * 
 * Evidence criteria:
 * - All prioritization decisions are recorded with full context
 * - Decisions can be reconstructed from the audit trail
 * - No "tribal knowledge" required to understand past decisions
 * - Complete provenance chain for any priority
 */

import { getSupabaseClient } from './supabaseClient';
import { getOrCreateSupabaseUser } from './userSync';

// ============================================================================
// Types
// ============================================================================

export type DecisionType = 
  | 'priority_set'       // Initial prioritization
  | 'priority_changed'   // Re-prioritization
  | 'item_added'         // Added to focus list
  | 'item_removed'       // Removed from focus list
  | 'cut_line_moved'     // Cut line adjustment
  | 'list_published'     // Focus list published
  | 'override_applied'   // Manual override
  | 'criteria_changed'   // Criteria weights changed
  | 'tool_selected'      // Tool choice made
  | 'session_completed'; // Session finished

export interface DecisionRecord {
  id: string;
  type: DecisionType;
  timestamp: string;
  sessionId?: string;
  processId?: string;
  focusListId?: string;
  
  // Who made the decision
  actorId: string;
  actorRole: string;
  
  // What was decided
  decision: {
    summary: string;
    details: Record<string, unknown>;
  };
  
  // Why (rationale)
  rationale?: string;
  
  // Context at time of decision
  context: {
    itemCount: number;
    toolUsed?: string;
    criteriaSnapshot?: Record<string, number>;
    participantCount?: number;
  };
  
  // Links to related records
  relatedDecisionIds?: string[];
  snapshotId?: string;
  
  // Workspace scope
  workspaceId: string;
}

/** One CSV row per decision: the columns a reader needs to find the decision again. */
export function decisionsToCsvRows(decisions: DecisionRecord[]) {
  return decisions.map((d) => ({
    timestamp: d.timestamp,
    type: d.type,
    summary: d.decision.summary,
    rationale: d.rationale ?? '',
    actor_id: d.actorId,
    actor_role: d.actorRole,
    item_count: d.context.itemCount,
    tool_used: d.context.toolUsed ?? '',
    session_id: d.sessionId ?? '',
    focus_list_id: d.focusListId ?? '',
    id: d.id,
  }));
}

export interface DecisionChain {
  entityId: string;
  entityType: 'item' | 'focus_list' | 'priority';
  entityName: string;
  decisions: DecisionRecord[];
  firstDecision: DecisionRecord;
  latestDecision: DecisionRecord;
}

export interface DecisionSummary {
  period: { start: string; end: string };
  totalDecisions: number;
  byType: Record<DecisionType, number>;
  keyDecisions: DecisionRecord[];
  participants: string[];
}

// ============================================================================
// In-Memory Store (In production, uses audit_events table)
// ============================================================================

const decisionsStore: Map<string, DecisionRecord> = new Map();

// ============================================================================
// Decision Recording
// ============================================================================

export async function recordDecision(
  input: Omit<DecisionRecord, 'id' | 'timestamp'>,
  clerkUserId: string
): Promise<DecisionRecord> {
  const supabaseUserId = await getOrCreateSupabaseUser(clerkUserId);
  
  const decision: DecisionRecord = {
    ...input,
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    actorId: supabaseUserId,
  };
  
  decisionsStore.set(decision.id, decision);
  
  // Also persist to audit_events for durability
  try {
    const supabase = getSupabaseClient();
    await supabase.from('audit_events').insert({
      event_type: `Decision:${decision.type}`,
      payload: decision,
      session_id: decision.sessionId || null,
      process_id: decision.processId || null,
      created_by: supabaseUserId,
    });
  } catch (error) {
    console.error('[DecisionMemory] Failed to persist decision:', error);
  }
  
  console.log('[DecisionMemory] Recorded decision:', decision.type, decision.decision.summary);
  return decision;
}

// ============================================================================
// Decision Retrieval
// ============================================================================

export async function getDecision(id: string): Promise<DecisionRecord | null> {
  return decisionsStore.get(id) || null;
}

export async function listDecisionsBySession(sessionId: string): Promise<DecisionRecord[]> {
  return Array.from(decisionsStore.values())
    .filter(d => d.sessionId === sessionId)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

export async function listDecisionsByProcess(processId: string): Promise<DecisionRecord[]> {
  return Array.from(decisionsStore.values())
    .filter(d => d.processId === processId)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

export async function listDecisionsByFocusList(focusListId: string): Promise<DecisionRecord[]> {
  return Array.from(decisionsStore.values())
    .filter(d => d.focusListId === focusListId)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

export async function listDecisionsByWorkspace(
  workspaceId: string,
  options?: { limit?: number; offset?: number; types?: DecisionType[] }
): Promise<DecisionRecord[]> {
  let decisions = Array.from(decisionsStore.values())
    .filter(d => d.workspaceId === workspaceId);
  
  if (options?.types && options.types.length > 0) {
    decisions = decisions.filter(d => options.types!.includes(d.type));
  }
  
  decisions = decisions.sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  
  if (options?.offset) {
    decisions = decisions.slice(options.offset);
  }
  
  if (options?.limit) {
    decisions = decisions.slice(0, options.limit);
  }
  
  return decisions;
}

// ============================================================================
// Decision Chain Reconstruction
// ============================================================================

/**
 * Reconstructs the complete decision chain for an entity.
 * This is the core capability that eliminates need for "oral history".
 */
export async function reconstructDecisionChain(
  entityId: string,
  entityType: 'item' | 'focus_list' | 'priority',
  entityName: string
): Promise<DecisionChain | null> {
  // Find all decisions that reference this entity
  const decisions = Array.from(decisionsStore.values())
    .filter(d => {
      const details = d.decision.details;
      
      switch (entityType) {
        case 'item':
          return details.itemId === entityId || 
                 (Array.isArray(details.itemIds) && details.itemIds.includes(entityId));
        case 'focus_list':
          return d.focusListId === entityId;
        case 'priority':
          return details.priorityId === entityId;
        default:
          return false;
      }
    })
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  
  if (decisions.length === 0) {
    return null;
  }
  
  return {
    entityId,
    entityType,
    entityName,
    decisions,
    firstDecision: decisions[0],
    latestDecision: decisions[decisions.length - 1],
  };
}

// ============================================================================
// Decision Summary
// ============================================================================

/**
 * Generates a human-readable summary of decisions over a time period.
 * Leaders can use this to understand what happened without asking.
 */
export async function generateDecisionSummary(
  workspaceId: string,
  startDate: Date,
  endDate: Date
): Promise<DecisionSummary> {
  const allDecisions = Array.from(decisionsStore.values())
    .filter(d => {
      const timestamp = new Date(d.timestamp);
      return d.workspaceId === workspaceId &&
             timestamp >= startDate &&
             timestamp <= endDate;
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  // Count by type
  const byType: Record<string, number> = {};
  for (const d of allDecisions) {
    byType[d.type] = (byType[d.type] || 0) + 1;
  }
  
  // Collect unique participants
  const participants = [...new Set(allDecisions.map(d => d.actorId))];
  
  // Identify key decisions (published, overrides, major changes)
  const keyTypes: DecisionType[] = ['list_published', 'override_applied', 'cut_line_moved'];
  const keyDecisions = allDecisions.filter(d => keyTypes.includes(d.type));
  
  return {
    period: {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    },
    totalDecisions: allDecisions.length,
    byType: byType as Record<DecisionType, number>,
    keyDecisions: keyDecisions.slice(0, 10), // Top 10
    participants,
  };
}

// ============================================================================
// Decision Narrative Generation
// ============================================================================

/**
 * Generates a human-readable narrative from a decision chain.
 * This is the artifact that replaces oral history.
 */
export function generateDecisionNarrative(chain: DecisionChain): string {
  const lines: string[] = [
    `# Decision History: ${chain.entityName}`,
    '',
    `**Entity Type:** ${chain.entityType}`,
    `**First Decision:** ${new Date(chain.firstDecision.timestamp).toLocaleDateString()}`,
    `**Latest Decision:** ${new Date(chain.latestDecision.timestamp).toLocaleDateString()}`,
    `**Total Decisions:** ${chain.decisions.length}`,
    '',
    '## Timeline',
    '',
  ];
  
  for (const decision of chain.decisions) {
    const date = new Date(decision.timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    
    lines.push(`### ${date} - ${formatDecisionType(decision.type)}`);
    lines.push('');
    lines.push(`**Actor:** ${decision.actorRole}`);
    lines.push(`**Summary:** ${decision.decision.summary}`);
    
    if (decision.rationale) {
      lines.push(`**Rationale:** ${decision.rationale}`);
    }
    
    if (decision.context.toolUsed) {
      lines.push(`**Tool Used:** ${decision.context.toolUsed}`);
    }
    
    lines.push('');
  }
  
  return lines.join('\n');
}

function formatDecisionType(type: DecisionType): string {
  const labels: Record<DecisionType, string> = {
    priority_set: 'Priority List',
    priority_changed: 'Priority Changed',
    item_added: 'Item Added',
    item_removed: 'Item Removed',
    cut_line_moved: 'Cut Line Moved',
    list_published: 'List Published',
    override_applied: 'Override Applied',
    criteria_changed: 'Criteria Changed',
    tool_selected: 'Tool Selected',
    session_completed: 'Session Completed',
  };
  
  return labels[type] || type;
}

// ============================================================================
// Helper Functions for Common Decision Types
// ============================================================================

export async function recordPriorityDecision(
  clerkUserId: string,
  workspaceId: string,
  itemId: string,
  itemName: string,
  newRank: number,
  previousRank: number | null,
  rationale: string,
  sessionId?: string,
  focusListId?: string
): Promise<DecisionRecord> {
  return recordDecision({
    type: previousRank === null ? 'priority_set' : 'priority_changed',
    sessionId,
    focusListId,
    actorId: '', // Will be set by recordDecision
    actorRole: 'prioritizer',
    workspaceId,
    decision: {
      summary: previousRank === null 
        ? `Set ${itemName} to rank #${newRank}`
        : `Changed ${itemName} from #${previousRank} to #${newRank}`,
      details: {
        itemId,
        itemName,
        previousRank,
        newRank,
        rankDelta: previousRank !== null ? newRank - previousRank : null,
      },
    },
    rationale,
    context: {
      itemCount: 1,
    },
  }, clerkUserId);
}

export async function recordPublishDecision(
  clerkUserId: string,
  workspaceId: string,
  focusListId: string,
  listName: string,
  itemCount: number,
  rationale: string
): Promise<DecisionRecord> {
  return recordDecision({
    type: 'list_published',
    focusListId,
    actorId: '', // Will be set by recordDecision
    actorRole: 'publisher',
    workspaceId,
    decision: {
      summary: `Published Item Pool "${listName}" with ${itemCount} ranked items`,
      details: {
        focusListId,
        listName,
        itemCount,
        publishedAt: new Date().toISOString(),
      },
    },
    rationale,
    context: {
      itemCount,
    },
  }, clerkUserId);
}

export async function recordOverrideDecision(
  clerkUserId: string,
  workspaceId: string,
  itemId: string,
  itemName: string,
  originalRank: number,
  overrideRank: number,
  rationale: string,
  focusListId?: string
): Promise<DecisionRecord> {
  return recordDecision({
    type: 'override_applied',
    focusListId,
    actorId: '', // Will be set by recordDecision
    actorRole: 'strategist',
    workspaceId,
    decision: {
      summary: `Manual override: moved ${itemName} from #${originalRank} to #${overrideRank}`,
      details: {
        itemId,
        itemName,
        originalRank,
        overrideRank,
        overrideType: 'manual',
      },
    },
    rationale,
    context: {
      itemCount: 1,
    },
  }, clerkUserId);
}


